/**
 * Black Friday 2025 - iCart Pack Selector Form (Tier 4)
 *
 * Displays a two-step selector form in the iCart for free pack selection.
 * Step 1: Choose a collection category (Outdoor, Wellness, Pet, Respiratory)
 * Step 2: Browse products from that collection and select one
 *
 * Depends on: window.BF2025CartProgress API from black-friday-2025-cart-manager.js
 * Depends on: window.BF_PACK_FORM_HTML template from black-friday-2025.liquid
 * Depends on: window.BF_PACK_COLLECTIONS array from black-friday-2025.liquid
 */

(function() {
  'use strict';

  // Configuration
  const SELECTORS = {
    icartList: '.icart-main .icart-list',
    packForm: '.bf-pack-form',
    categoriesStep: '.bf-pack-form-step--categories',
    productsStep: '.bf-pack-form-step--products',
    categoryBtn: '.bf-pack-category-btn',
    backBtn: '.bf-pack-back-btn',
    productsGrid: '.bf-pack-products-grid',
    productsLoading: '.bf-pack-products-loading',
    productsTitle: '.bf-pack-form-title--products'
  };

  let observer = null;
  let currentPackForm = null;
  let isProcessing = false;
  let currentStep = 'categories'; // 'categories' or 'products'
  let selectedCollection = null;

  /**
   * Check if pack form should be shown
   */
  function shouldShowForm() {
    if (!window.BF2025CartProgress || typeof window.BF2025CartProgress.shouldShowPackForm !== 'function') {
      console.warn('[BF Pack Form] Cart manager not initialized');
      return false;
    }
    return window.BF2025CartProgress.shouldShowPackForm();
  }

  /**
   * Show the pack form
   */
  function showForm() {
    if (currentPackForm) {
      currentPackForm.classList.remove('bf-2025--hidden');
      console.log('[BF Pack Form] Form shown');
    }
  }

  /**
   * Hide the pack form
   */
  function hideForm() {
    if (currentPackForm) {
      currentPackForm.classList.add('bf-2025--hidden');
      console.log('[BF Pack Form] Form hidden');
    }
  }

  /**
   * Show categories step
   */
  function showCategoriesStep() {
    if (!currentPackForm) return;

    const categoriesStep = currentPackForm.querySelector(SELECTORS.categoriesStep);
    const productsStep = currentPackForm.querySelector(SELECTORS.productsStep);

    if (categoriesStep) categoriesStep.classList.remove('bf-2025--hidden');
    if (productsStep) productsStep.classList.add('bf-2025--hidden');

    currentStep = 'categories';
    selectedCollection = null;
    console.log('[BF Pack Form] Showing categories step');
  }

  /**
   * Show products step
   */
  function showProductsStep() {
    if (!currentPackForm) return;

    const categoriesStep = currentPackForm.querySelector(SELECTORS.categoriesStep);
    const productsStep = currentPackForm.querySelector(SELECTORS.productsStep);

    if (categoriesStep) categoriesStep.classList.add('bf-2025--hidden');
    if (productsStep) productsStep.classList.remove('bf-2025--hidden');

    currentStep = 'products';
    console.log('[BF Pack Form] Showing products step');
  }

  /**
   * Show loading state in products grid
   */
  function showLoading() {
    if (!currentPackForm) return;

    const loading = currentPackForm.querySelector(SELECTORS.productsLoading);
    const grid = currentPackForm.querySelector(SELECTORS.productsGrid);

    if (loading) loading.classList.remove('bf-2025--hidden');
    if (grid) grid.innerHTML = '';
  }

  /**
   * Hide loading state
   */
  function hideLoading() {
    if (!currentPackForm) return;

    const loading = currentPackForm.querySelector(SELECTORS.productsLoading);
    if (loading) loading.classList.add('bf-2025--hidden');
  }

  /**
   * Fetch products from a collection
   * Only returns products with the "free1pack" tag
   */
  async function fetchCollectionProducts(collectionHandle) {
    console.log(`[BF Pack Form] Fetching products from collection: ${collectionHandle}`);

    try {
      const response = await fetch(`/collections/${collectionHandle}/products.json`);
      if (!response.ok) {
        throw new Error(`Failed to fetch collection: ${response.statusText}`);
      }

      const data = await response.json();
      // Filter to only show products with "free1pack" tag
      const filteredProducts = (data.products || []).filter(product =>
        product.tags && product.tags.includes('free1pack')
      );
      console.log(`[BF Pack Form] Fetched ${data.products.length} products, ${filteredProducts.length} with free1pack tag`);
      return filteredProducts;
    } catch (error) {
      console.error('[BF Pack Form] Failed to fetch collection products:', error);
      return [];
    }
  }

  /**
   * Render product cards in the grid
   */
  function renderProductGrid(products) {
    if (!currentPackForm) return;

    const grid = currentPackForm.querySelector(SELECTORS.productsGrid);
    if (!grid) return;

    if (products.length === 0) {
      grid.innerHTML = '<p class="bf-pack-no-products">No products available in this collection.</p>';
      return;
    }

    const html = products.map(product => {
      const image = product.images && product.images.length > 0
        ? product.images[0].src
        : 'https://cdn.shopify.com/s/files/1/0601/2263/1740/files/placeholder.png';

      // Find the appropriate variant
      let variantId = null;
      if (product.variants && product.variants.length > 0) {
        if (product.variants.length === 1) {
          // Single variant - use it
          variantId = product.variants[0].id;
        } else {
          // Multiple variants - find the "1 Pack" variant
          const onePackVariant = product.variants.find(v =>
            v.title && v.title.toLowerCase().includes('1 pack')
          );
          if (onePackVariant) {
            variantId = onePackVariant.id;
          }
          // If no "1 Pack" variant found, variantId stays null and product is skipped
        }
      }

      if (!variantId) return '';

      return `
        <div class="bf-pack-product-card" data-variant-id="${variantId}" data-product-title="${product.title}">
          <div class="bf-pack-product-image">
            <img src="${image}" alt="${product.title}" loading="lazy">
          </div>
          <div class="bf-pack-product-title">${product.title}</div>
          <button class="bf-pack-product-select-btn" type="button">Select</button>
        </div>
      `;
    }).join('');

    grid.innerHTML = html;

    // Attach event listeners to product cards (entire card is clickable)
    const productCards = grid.querySelectorAll('.bf-pack-product-card');
    productCards.forEach(card => {
      card.addEventListener('click', handleProductSelect);
    });

    console.log(`[BF Pack Form] Rendered ${products.length} product cards`);
  }

  /**
   * Add selected pack to cart
   */
  async function addPackToCart(variantId, productTitle) {
    if (isProcessing) {
      console.warn('[BF Pack Form] Already processing, please wait');
      return;
    }

    console.log(`[BF Pack Form] Adding pack "${productTitle}" (variant ${variantId}) to cart`);
    isProcessing = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: String(variantId),
            quantity: 1,
            properties: {
              _bf_free_gift: 'tier4'
            }
          }]
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to add pack: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[BF Pack Form] Pack added successfully:', result);

      // Hide form after successful addition
      hideForm();

      // Reset to categories step for next time
      showCategoriesStep();

      // Refresh iCart UI
      if (typeof window.prepareDataForDisplayWidgetByRule === 'function') {
        console.log('[BF Pack Form] Refreshing iCart UI');
        window.prepareDataForDisplayWidgetByRule();
      }

    } catch (error) {
      console.error('[BF Pack Form] Failed to add pack:', error);
      alert('Failed to add pack to cart. Please try again.');
    } finally {
      isProcessing = false;
    }
  }

  /**
   * Handle category button click
   */
  async function handleCategoryClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const collectionHandle = button.getAttribute('data-collection');

    if (!collectionHandle) {
      console.error('[BF Pack Form] No collection handle found on button');
      return;
    }

    // Find collection name
    const collection = window.BF_PACK_COLLECTIONS.find(c => c.handle === collectionHandle);
    const collectionName = collection ? collection.name : collectionHandle;

    console.log(`[BF Pack Form] Category selected: ${collectionName}`);
    selectedCollection = collectionHandle;

    // Update title
    const title = currentPackForm.querySelector(SELECTORS.productsTitle);
    if (title) {
      title.textContent = `Choose your free ${collectionName}:`;
    }

    // Show products step with loading
    showProductsStep();
    showLoading();

    // Fetch and render products
    const products = await fetchCollectionProducts(collectionHandle);
    hideLoading();
    renderProductGrid(products);
  }

  /**
   * Handle back button click
   */
  function handleBackClick(event) {
    event.preventDefault();
    event.stopPropagation();

    console.log('[BF Pack Form] Back button clicked');
    showCategoriesStep();
  }

  /**
   * Handle product select button click
   */
  function handleProductSelect(event) {
    event.preventDefault();
    event.stopPropagation();

    const card = event.target.closest('.bf-pack-product-card');
    if (!card) return;

    const variantId = card.getAttribute('data-variant-id');
    const productTitle = card.getAttribute('data-product-title');

    if (!variantId) {
      console.error('[BF Pack Form] No variant ID found on card');
      return;
    }

    addPackToCart(variantId, productTitle);
  }

  /**
   * Create and inject the pack form into iCart
   */
  function injectPackForm() {
    const icartList = document.querySelector(SELECTORS.icartList);
    if (!icartList) {
      console.warn('[BF Pack Form] .icart-list not found');
      return;
    }

    // Check if we already have a pack form
    const existingForm = icartList.querySelector(SELECTORS.packForm);
    if (existingForm) {
      currentPackForm = existingForm;
      return;
    }

    // Check if template HTML is available
    if (!window.BF_PACK_FORM_HTML) {
      console.warn('[BF Pack Form] Template HTML not found in window.BF_PACK_FORM_HTML');
      return;
    }

    // Create form from template
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = window.BF_PACK_FORM_HTML.trim();
    const packForm = tempDiv.firstChild;

    if (!packForm) {
      console.warn('[BF Pack Form] Failed to create pack form element');
      return;
    }

    // Find the socks form and insert pack form after it, or after progress bar
    const socksForm = icartList.querySelector('.bf-socks-form');
    const progressBar = icartList.querySelector('.bf-progress-bar');

    if (socksForm && socksForm.nextSibling) {
      icartList.insertBefore(packForm, socksForm.nextSibling);
    } else if (progressBar && progressBar.nextSibling) {
      icartList.insertBefore(packForm, progressBar.nextSibling);
    } else {
      // Fallback: insert after first child
      if (icartList.children.length > 1) {
        icartList.insertBefore(packForm, icartList.children[1]);
      } else {
        icartList.appendChild(packForm);
      }
    }

    currentPackForm = packForm;

    // Attach event listeners to category buttons
    const categoryButtons = packForm.querySelectorAll(SELECTORS.categoryBtn);
    categoryButtons.forEach(button => {
      button.addEventListener('click', handleCategoryClick);
    });

    // Attach event listener to back button
    const backBtn = packForm.querySelector(SELECTORS.backBtn);
    if (backBtn) {
      backBtn.addEventListener('click', handleBackClick);
    }

    console.log('[BF Pack Form] Form injected into iCart with', categoryButtons.length, 'category buttons');
  }

  /**
   * Remove the pack form from iCart
   */
  function removePackForm() {
    if (currentPackForm && currentPackForm.parentNode) {
      currentPackForm.parentNode.removeChild(currentPackForm);
      currentPackForm = null;
      console.log('[BF Pack Form] Form removed');
    }
  }

  /**
   * Update form visibility based on cart state
   */
  function updateFormVisibility() {
    const shouldShow = shouldShowForm();

    if (shouldShow) {
      // Ensure form exists
      if (!currentPackForm) {
        injectPackForm();
      }

      // Show the form
      if (currentPackForm) {
        showForm();
      }
    } else {
      // Hide the form
      hideForm();

      // Reset to categories step when hiding
      if (currentPackForm) {
        showCategoriesStep();
      }
    }
  }

  /**
   * Wait for an element to appear in the DOM using MutationObserver
   */
  function waitForElement(selector, callback) {
    const element = document.querySelector(selector);
    if (element) {
      callback(element);
      return;
    }

    const observer = new MutationObserver((mutations, obs) => {
      const element = document.querySelector(selector);
      if (element) {
        obs.disconnect();
        callback(element);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Initialize MutationObserver to watch for cart changes
   */
  function initObserver() {
    const icartItems = document.querySelector('.icart-main .icart-items');
    if (!icartItems) {
      console.log('[BF Pack Form] .icart-items not found, waiting for it to appear...');
      return false;
    }

    // Create observer to watch cart items
    observer = new MutationObserver(() => {
      updateFormVisibility();
    });

    observer.observe(icartItems, {
      childList: true,
      subtree: true,
      characterData: true
    });

    console.log('[BF Pack Form] MutationObserver initialized');

    // Initial update
    updateFormVisibility();

    return true;
  }

  /**
   * Initialize the pack form system
   */
  function init() {
    console.log('[BF Pack Form] Initializing...');

    // Check feature flag first
    if (!window.BF_TIER4_ENABLED) {
      console.log('[BF Pack Form] Tier 4 feature is disabled. Skipping initialization.');
      return;
    }

    // Wait for DOM
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    // Check for dependencies
    if (!window.BF2025CartProgress) {
      console.warn('[BF Pack Form] Waiting for cart manager...');
      setTimeout(init, 500);
      return;
    }

    if (!window.BF_PACK_FORM_HTML) {
      console.warn('[BF Pack Form] Waiting for template HTML...');
      setTimeout(init, 500);
      return;
    }

    if (!window.BF_PACK_COLLECTIONS) {
      console.warn('[BF Pack Form] Waiting for collections data...');
      setTimeout(init, 500);
      return;
    }

    // Try to initialize observer
    const success = initObserver();
    if (!success) {
      // Wait for iCart to appear using MutationObserver (reactive, no polling)
      waitForElement('.icart-main .icart-items', () => {
        console.log('[BF Pack Form] .icart-items appeared, initializing observer');
        initObserver();
      });
    }
  }

  // Start initialization
  init();

  // Also listen for custom cart update events
  if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    document.addEventListener(window.PUB_SUB_EVENTS.cartUpdate, () => {
      updateFormVisibility();
    });
  }

  // Expose refresh function for manual updates if needed
  window.BF2025PackForm = {
    refresh: updateFormVisibility
  };

})();
